/**
 * Custom React hook for managing and configuring recipe sliders within the order flow module.
 *
 * This hook orchestrates the logic for rendering and interacting with sliders that allow users
 * to customize meal recipes. It integrates with the customization context, order flow context,
 * and recipe module configuration to provide a cohesive slider experience.
 * @param SlidersProps
 * @returns UseSlidersReturnType
 *
 */
import { useMediaQuery, useTheme } from "@mui/material";
import moment from "moment";
import { useContext, useMemo } from "react";
import { CustomizationContext } from "../../../../../Shared/components/CustomizeModal/CustomizationContext";
import { useOrderFlow } from "../../../../OrderFlowProvider/OrderFlowContext";
import { isAnyMealSkippedForGivenDay } from "../../DayFilter/useDayFilter";
import { useRecipeModuleConfig } from "../hooks/useRecipeModuleConfig"; // Adjust path
import { buildSliders } from "../utils/buildSliders";
import { restoreMeals as buildRestoreMeals } from "../utils/RecipeModule-utils";
export const useSliders = ({ programMealsWithRecipes, renderAllRecipesSlider, sequenceNumber, recipeHandlerProps_One, }) => {
    const theme = useTheme();
    const tabletAndAbove = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
    const { setMeals, setAssignmentId, setIsAddDisabled, setQuantity } = useContext(CustomizationContext);
    const { store, dispatch } = useOrderFlow();
    const { moduleConfig, enableCustomerSorting, recipeImageBaseUrl, recipeAutoSelection, enableCostSorting, paperSettings, recipeSortOption, } = useRecipeModuleConfig(sequenceNumber);
    const selectedDay = store.cart.selectedDay;
    const isAnyMealSkippedForDay = isAnyMealSkippedForGivenDay(store.cart.meals)(moment(selectedDay));
    const restoreMeals = buildRestoreMeals(dispatch, store.cart.meals, moment(selectedDay));
    const slidersData = useMemo(() => buildSliders({
        isAnyMealSkippedForDay,
        moduleConfig,
        programMealsWithRecipes,
        recipeAutoSelection,
        recipeHandlerProps_One,
        recipeImageBaseUrl,
        renderAllRecipesSlider,
        setAssignmentId,
        setIsAddDisabled,
        setMeals,
        setQuantity,
        store,
    }), [
        isAnyMealSkippedForDay,
        moduleConfig,
        programMealsWithRecipes,
        recipeAutoSelection,
        recipeHandlerProps_One,
        recipeImageBaseUrl,
        renderAllRecipesSlider,
        setAssignmentId,
        setIsAddDisabled,
        setMeals,
        setQuantity,
        store,
    ]);
    const { programMealSliders, allRecipesSlider, anyRecipesAvailable } = slidersData;
    return {
        allRecipesSlider,
        anyRecipesAvailable,
        enableCostSorting,
        enableCustomerSorting,
        isAnyMealSkippedForDay,
        moduleConfig,
        paperSettings,
        programMealSliders,
        recipeSortOption,
        restoreMeals,
        selectedDay,
        tabletAndAbove,
    };
};
